/**
 * Small helpers for transient network / server errors (refresh flakiness, cold starts).
 */

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * True when a retry might succeed (not auth/client errors).
 */
export function isTransientRequestError(error) {
    if (!error) return false;
    const status = error.response?.status;
    if (status === 401 || status === 403 || status === 400 || status === 404 || status === 422) {
        return false;
    }
    if (error.code === 'ECONNABORTED') return true;
    if (!error.response) return true;
    if (status === 408 || status === 429) return true;
    if (status >= 500 && status <= 504) return true;
    return false;
}

/**
 * Run async fn with exponential backoff on retriable failures.
 */
export async function withRetries(fn, { retries = 3, baseDelayMs = 400, shouldRetry = isTransientRequestError } = {}) {
    let lastError;
    for (let attempt = 0; attempt < retries; attempt += 1) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            if (attempt >= retries - 1 || !shouldRetry(error)) {
                throw error;
            }
            await sleep(baseDelayMs * 2 ** attempt);
        }
    }
    throw lastError;
}
